<script lang="ts">
  import { AI_MODELS } from '$lib/config/aiModels.config'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import type { AIModelInfo } from '$lib/types/ai'

  interface Props {
    show: boolean
    promptInput: string
    placeholder?: string
    onSubmit: () => void
    onClose?: () => void
    modelId?: string  // Make optional since we'll use settings store
    onModelChange?: (modelId: string) => void  // Make optional
  }

  let { 
    show,
    promptInput = $bindable(),
    placeholder = "전체 텍스트를 어떻게 변경할까요?",
    onSubmit,
    onClose,
    modelId = $bindable(),  
    onModelChange
  }: Props = $props()

  let showModelDropdown = $state(false)
  let promptTextareaRef = $state<HTMLTextAreaElement | null>(null)
  let modelSelectorRef = $state<HTMLDivElement | null>(null)
  let generationBarRef = $state<HTMLDivElement | null>(null)
  let ignoreNextClick = $state(false) // Flag to ignore click after model selection
  let dropdownDirection = $state<'up' | 'down'>('down') // Dropdown direction

  // Get settings reactively
  const settings = $derived($settingsStore)
  
  // Use settings model if modelId prop is not provided
  const currentModelId = $derived(modelId || settings.ai?.selectedModel || 'openai/gpt-oss-120b')
  
  // Get all available AI models from configuration
  const allModels = $derived.by(() => {
    const models: AIModelInfo[] = []
    const providers = settings.aiProviders || []
    
    // Get enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    // Add models from each enabled provider
    for (const provider of enabledProviders) {
      const providerModels = AI_MODELS[provider.id] || []
      models.push(...providerModels)
    }
    
    // If OpenRouter is not enabled, still include it (free models)
    if (!enabledProviders.some(p => p.id === 'openrouter')) {
      const openrouterModels = AI_MODELS['openrouter'] || []
      models.push(...openrouterModels)
    }
    
    return models
  })

  // Get current model
  const currentModel = $derived(
    allModels.find(m => m.id === currentModelId)
  )

  // Sort models with recommended first
  function sortedModels() {
    return [...allModels].sort((a, b) => {
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      return 0
    })
  }

  // Check if dropdown should open upward
  function checkDropdownDirection() {
    if (!modelSelectorRef) return
    
    const rect = modelSelectorRef.getBoundingClientRect()
    const dropdownHeight = 250 // Approximate max height of dropdown
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    
    // Open upward if not enough space below and more space above
    dropdownDirection = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down'
  }
  
  // Toggle model dropdown
  function toggleModelDropdown(e: MouseEvent) {
    e.stopPropagation()
    showModelDropdown = !showModelDropdown
    if (showModelDropdown) {
      checkDropdownDirection()
    }
  }

  // Select AI model
  function selectModel(model: AIModelInfo) {
    // Update both local and global state
    if (onModelChange) {
      onModelChange(model.id)
    }
    // Always update global settings
    settingsActions.updateAISettings({ selectedModel: model.id })
    showModelDropdown = false
    
    // Set flag to ignore next click to prevent closing the prompt
    ignoreNextClick = true
    setTimeout(() => {
      ignoreNextClick = false
    }, 200) // Reset flag after 200ms
  }

  // Handle prompt input with auto-resize
  function handlePromptInput(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    textarea.style.height = 'auto'
    const newHeight = Math.min(textarea.scrollHeight, 80)
    textarea.style.height = newHeight + 'px'
  }

  // Handle keyboard shortcuts
  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (promptInput.trim()) {
        onSubmit()
      }
    } else if (e.key === 'Escape') {
      onClose?.()
    }
  }

  // Focus on textarea when shown
  $effect(() => {
    if (show && promptTextareaRef) {
      setTimeout(() => promptTextareaRef?.focus(), 50)
    }
  })

  // Close dropdown on outside click
  $effect(() => {
    if (showModelDropdown) {
      const handleClickOutside = (e: MouseEvent) => {
        if (modelSelectorRef && !modelSelectorRef.contains(e.target as Node)) {
          showModelDropdown = false
        }
      }
      
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  })
  
  // Close bulk prompt on outside click
  $effect(() => {
    if (show) {
      const handleClickOutside = (e: MouseEvent) => {
        // Ignore click if flag is set (right after model selection)
        if (ignoreNextClick) {
          return
        }
        
        const target = e.target as HTMLElement
        
        // Check if clicked on the model selector or dropdown
        if (modelSelectorRef && modelSelectorRef.contains(target)) {
          // Don't close if clicking on model selector
          return
        }
        
        // Check if clicked outside the generation bar
        if (generationBarRef && !generationBarRef.contains(target)) {
          // Check if it's not the prompt button itself
          const promptButton = target.closest('.ai-tool-btn')
          if (!promptButton || !promptButton.textContent?.includes('AI')) {
            onClose?.()
          }
        }
      }
      
      // Add slight delay to prevent immediate close on open
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 10)
      
      return () => {
        clearTimeout(timer)
        document.removeEventListener('click', handleClickOutside)
      }
    }
  })
</script>

{#if show}
  <div class="bulk-generation-bar" bind:this={generationBarRef}>
    <div class="bulk-chat-container">
      <!-- Main textarea -->
      <textarea
        bind:this={promptTextareaRef}
        bind:value={promptInput}
        class="bulk-prompt-input"
        {placeholder}
        maxlength="200"
        oninput={handlePromptInput}
        onkeydown={handleKeyDown}
        rows="1"
      ></textarea>
      
      <!-- Bottom controls -->
      <div class="bulk-bottom-controls">
        <!-- Model selector at bottom left -->
        <div class="bulk-model-selector" bind:this={modelSelectorRef}>
          <button
            class="bulk-model-btn"
            onclick={(e) => toggleModelDropdown(e)}
            title="AI 모델 선택"
          >
            {#if currentModel}
              <span class="bulk-model-name">{currentModel.name}</span>
            {:else}
              <span class="bulk-model-name">AI 모델</span>
            {/if}
            <svg class="bulk-chevron-icon" class:rotated={showModelDropdown} width="5" height="5" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" stroke="currentColor" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <!-- Model dropdown -->
          {#if showModelDropdown}
            <div class="bulk-model-dropdown" class:dropdown-up={dropdownDirection === 'up'}>
              <div class="bulk-model-list">
                {#each sortedModels() as model}
                  <button
                    class="bulk-model-item"
                    class:selected={currentModelId === model.id}
                    onclick={() => selectModel(model)}
                  >
                    <div class="bulk-item-info">
                      <div class="bulk-item-name">
                        {model.name}
                        {#if model.recommended}
                          <span class="bulk-recommended-badge">추천</span>
                        {/if}
                      </div>
                    </div>
                    {#if currentModelId === model.id}
                      <svg class="bulk-check-icon" width="10" height="10" viewBox="0 0 14 14">
                        <path 
                          d="M2 7l3 3 7-7" 
                          stroke="#6366f1" 
                          fill="none" 
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    {/if}
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        
        <!-- Right side controls -->
        <div class="bulk-right-controls">
          <!-- Character count -->
          {#if promptInput.length >= 50}
            <span class="bulk-char-count">{promptInput.length}/200</span>
          {/if}
          
          <!-- Send button -->
          <button
            class="bulk-send-btn"
            onclick={onSubmit}
            disabled={!promptInput.trim()}
            title="생성 (Enter)"
            aria-label="생성"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Bulk Prompt Input Styles (matching card design) */
  .bulk-generation-bar {
    padding: 2px 0;
    background: transparent;
    margin-top: 1px;
    position: relative;
    z-index: 100;
  }
  
  .bulk-chat-container {
    position: relative;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 10px;
    padding: 8px 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.2s;
  }
  
  .bulk-chat-container:focus-within {
    border-color: var(--border-secondary, #d1d5db);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  /* Main textarea */
  .bulk-prompt-input {
    width: 100%;
    padding: 1px 8px;
    padding-bottom: 24px; /* Space for bottom controls */
    font-size: 12px;
    line-height: 1.4;
    border: none;
    background: transparent;
    color: var(--text-primary, #1a1a1a);
    outline: none;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 20px;
    max-height: 80px;
    overflow-y: auto;
  }
  
  .bulk-prompt-input::placeholder {
    color: var(--text-tertiary, #9ca3af);
    font-size: 12px;
  }
  
  /* Custom scrollbar */
  .bulk-prompt-input::-webkit-scrollbar {
    width: 4px;
  }
  
  .bulk-prompt-input::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .bulk-prompt-input::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 2px;
  }
  
  /* Bottom controls */
  .bulk-bottom-controls {
    position: absolute;
    bottom: 5px;
    left: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  /* Model selector */
  .bulk-model-selector {
    position: relative;
    z-index: 1000;
  }
  
  .bulk-model-btn {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 3px 6px;
    background: transparent;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.15s;
    color: #6b7280;
  }
  
  .bulk-model-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  .bulk-model-name {
    font-size: 10px;
    font-weight: 500;
  }
  
  .bulk-chevron-icon {
    transition: transform 0.2s;
    opacity: 0.6;
  }
  
  .bulk-chevron-icon.rotated {
    transform: rotate(180deg);
  }
  
  /* Model dropdown */
  .bulk-model-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 2px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 200px;
    min-width: 150px;
    overflow: hidden;
    z-index: 9999;
  }
  
  /* Dropdown opens upward when near bottom */
  .bulk-model-dropdown.dropdown-up {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 2px;
  }
  
  .bulk-model-list {
    max-height: 200px;
    overflow-y: auto;
    padding: 3px;
  }
  
  /* Hide scrollbar but keep functionality */
  .bulk-model-list::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  
  .bulk-model-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 6px;
    width: 100%;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    text-align: left;
  }
  
  .bulk-model-item:hover {
    background: #f3f4f6;
  }
  
  .bulk-model-item.selected {
    background: #ede9fe;
  }
  
  .bulk-item-info {
    flex: 1;
  }
  
  .bulk-item-name {
    font-size: 10px;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 3px;
  }
  
  .bulk-recommended-badge {
    font-size: 8px;
    padding: 0px 3px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 3px;
    font-weight: 600;
  }
  
  .bulk-check-icon {
    flex-shrink: 0;
  }
  
  .bulk-right-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  /* Character count */
  .bulk-char-count {
    font-size: 10px;
    color: #9ca3af;
    font-family: 'SF Mono', Monaco, monospace;
    opacity: 0.7;
  }
  
  /* Send button */
  .bulk-send-btn {
    width: 24px;
    height: 24px;
    border-radius: 5px;
    background: #5856d6;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  
  .bulk-send-btn:hover:not(:disabled) {
    background: #4744c9;
    transform: scale(1.05);
  }
  
  .bulk-send-btn:disabled {
    background: #e5e7eb;
    cursor: not-allowed;
  }
  
  .bulk-send-btn svg {
    width: 12px;
    height: 12px;
    color: white;
  }
  
  .bulk-send-btn:disabled svg {
    color: #9ca3af;
  }
  
  /* Dark theme adjustments */
  :global(.dark-theme) .bulk-chat-container:focus-within {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }
  
  :global(.dark-theme) .bulk-prompt-input::-webkit-scrollbar-thumb {
    background: #4b5563;
  }
  
  :global(.dark-theme) .bulk-model-btn {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .bulk-model-btn:hover {
    background: #374151;
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .bulk-model-dropdown {
    background: #1f2937;
    border-color: #374151;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  }
  
  :global(.dark-theme) .bulk-model-item {
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .bulk-model-item:hover {
    background: #374151;
  }
  
  :global(.dark-theme) .bulk-model-item.selected {
    background: rgba(99, 102, 241, 0.2);
  }
  
  :global(.dark-theme) .bulk-item-name {
    color: #e5e7eb;
  }
  
  :global(.dark-theme) .bulk-recommended-badge {
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
  }
  
  :global(.dark-theme) .bulk-send-btn {
    background: #6366f1;
  }
  
  :global(.dark-theme) .bulk-send-btn:hover:not(:disabled) {
    background: #7c7aff;
  }
  
  :global(.dark-theme) .bulk-send-btn:disabled {
    background: #374151;
    color: #6b7280;
  }
  
  /* Auto theme (system dark mode) support for model dropdown */
  @media (prefers-color-scheme: dark) {
    .bulk-model-btn {
      color: #9ca3af;
    }
    
    .bulk-model-btn:hover {
      background: #374151;
      color: #e5e7eb;
    }
    
    .bulk-model-dropdown {
      background: #1f2937;
      border-color: #374151;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
    
    .bulk-model-item {
      color: #e5e7eb;
    }
    
    .bulk-model-item:hover {
      background: #374151;
    }
    
    .bulk-model-item.selected {
      background: rgba(99, 102, 241, 0.2);
    }
    
    .bulk-item-name {
      color: #e5e7eb;
    }
    
    .bulk-recommended-badge {
      background: rgba(251, 191, 36, 0.2);
      color: #fbbf24;
    }
    
    .bulk-send-btn {
      background: #6366f1;
    }
    
    .bulk-send-btn:hover:not(:disabled) {
      background: #7c7aff;
    }
    
    .bulk-send-btn:disabled {
      background: #374151;
      color: #6b7280;
    }
  }
</style>